import React, { useState, useEffect } from 'react';
import { 
  Video, 
  Users, 
  Clock, 
  Eye,
  Key,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import platformApi from '../services/platformApi';

interface AnalyticsData {
  totalCalls: number;
  totalRooms: number;
  activeRooms: number;
  totalParticipants: number;
  successRate: number;
  avgDuration: number;
}

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, trend, icon }) => {
  const trendColors = {
    up: 'text-success',
    down: 'text-error',
    neutral: 'text-light-text-secondary dark:text-dark-text-secondary'
  };

  return (
    <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border hover:shadow-lg dark:hover:shadow-dark-lg transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
            {title}
          </p>
          <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
            {value}
          </p>
          <p className={`text-sm ${trendColors[trend]} mt-1`}>
            {change}
          </p>
        </div>
        <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
          <div className="text-primary-500">
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
};

export const DashboardOverview: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [recentActivity, setRecentActivity] = useState<Array<{
    endpoint: string;
    method: string;
    status_code: number;
    timestamp: string;
    response_time_ms: number;
  }>>([]);
  const [dailyUsage, setDailyUsage] = useState<Array<{
    date: string;
    apiCalls: number;
    roomsCreated: number;
    participants: number;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const data = await platformApi.getAnalytics(30);
        // Convert API response to match component interface
        // Note: Backend returns 'analytics' not 'summary', and 'successRate' not 'avgErrorRate'
        setAnalyticsData({
          totalCalls: data.analytics.totalCalls,
          totalRooms: data.analytics.totalRooms,
          activeRooms: data.analytics.activeRooms,
          totalParticipants: data.analytics.totalParticipants,
          successRate: data.analytics.successRate, // Already success rate, not error rate
          avgDuration: data.analytics.avgDuration
        });
        // Set real recent activity data and daily usage
        setRecentActivity(data.recentActivity || []);
        setDailyUsage(data.dailyUsage || []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  // Use real data or fallback for loading/error states
  const displayData = analyticsData || {
    totalCalls: 0,
    totalRooms: 0,
    activeRooms: 0,
    totalParticipants: 0,
    successRate: 100,
    avgDuration: 0
  };

  const stats = [
    {
      title: 'Total API Calls',
      value: loading ? '...' : displayData.totalCalls.toLocaleString(),
      change: loading ? 'Loading...' : 'Real-time data',
      trend: 'up' as const,
      icon: <BarChart3 size={24} />
    },
    {
      title: 'Total Rooms',
      value: loading ? '...' : displayData.totalRooms.toLocaleString(),
      change: loading ? 'Loading...' : `${displayData.activeRooms} currently active`,
      trend: 'up' as const,
      icon: <Video size={24} />
    },
    {
      title: 'Participants',
      value: loading ? '...' : displayData.totalParticipants.toLocaleString(),
      change: loading ? 'Loading...' : 'Total joined',
      trend: 'up' as const,
      icon: <Users size={24} />
    },
    {
      title: 'Success Rate',
      value: loading ? '...' : `${displayData.successRate}%`,
      change: loading ? 'Loading...' : `Avg duration: ${displayData.avgDuration}m`,
      trend: 'up' as const,
      icon: <Clock size={24} />
    }
  ];

  // Helper function to format recent activity from API data
  const formatRecentActivity = (activities: typeof recentActivity) => {
    return activities.slice(0, 5).map(activity => {
      // Determine action description and type based on endpoint
      let action = '';
      let type = 'info';
      
      if (activity.endpoint.includes('/rooms/create')) {
        action = 'Room created';
        type = 'video';
      } else if (activity.endpoint.includes('/tokens/generate')) {
        action = 'Token generated';
        type = 'key';
      } else if (activity.endpoint.includes('/rooms/join')) {
        action = 'Room joined';
        type = 'user';
      } else if (activity.endpoint.includes('/analytics')) {
        action = 'Analytics requested';
        type = 'info';
      } else if (activity.endpoint.includes('/rooms/delete')) {
        action = 'Room deleted';
        type = 'video';
      } else {
        action = `${activity.method} ${activity.endpoint}`;
        type = activity.status_code >= 400 ? 'warning' : 'info';
      }
      
      // Format timestamp to relative time
      const timeAgo = formatTimeAgo(activity.timestamp);
      
      return { action, time: timeAgo, type };
    });
  };
  
  // Helper function to format timestamps as relative time
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return past.toLocaleDateString();
  };
  
  const displayedRecentActivity = formatRecentActivity(recentActivity);

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">Failed to load dashboard data</p>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-2">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'key': return <Key size={16} className="text-primary-500" />;
      case 'video': return <Video size={16} className="text-success" />;
      case 'user': return <Users size={16} className="text-info" />;
      case 'warning': return <AlertCircle size={16} className="text-warning" />;
      case 'info': return <BarChart3 size={16} className="text-blue-500" />;
      default: return <Eye size={16} className="text-light-text-secondary dark:text-dark-text-secondary" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
          Dashboard Overview
        </h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
          Monitor your Customer Connects API usage and performance
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* API Usage Chart */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
            API Usage & Participants Trend
        </h2>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Recent 7-day activity overview
          </p>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="h-64">
              {/* Simple dual-axis chart */}
              <div className="flex items-end justify-between h-full space-x-2">
                {dailyUsage.length === 0 ? (
                  <div className="text-center w-full">
                    <p className="text-light-text-secondary dark:text-dark-text-secondary">
                      No API usage data available
                    </p>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-2">
                      Start making API calls to see usage trends here
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Chart bars using real daily usage data */}
                    {dailyUsage.slice(0, 7).reverse().map((data, index) => {
                      const maxCalls = Math.max(...dailyUsage.map(d => d.apiCalls), 1);
                      const maxParticipants = Math.max(...dailyUsage.map(d => d.participants), 1);
                      
                      const callHeight = (data.apiCalls / maxCalls) * 80;
                      const participantHeight = (data.participants / maxParticipants) * 80;
                      
                      // Format date to day name
                      const dayName = new Date(data.date).toLocaleDateString('en-US', { weekday: 'short' });
                      
                      return (
                        <div key={data.date} className="flex-1 flex flex-col items-center space-y-2">
                          <div className="flex items-end space-x-1 h-48">
                            {/* API Calls Bar */}
                            <div className="relative group">
                              <div 
                                className="w-6 bg-primary-500 rounded-t opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                style={{ height: `${callHeight}%` }}
                              >
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="bg-dark-bg-primary dark:bg-light-bg-primary text-dark-text-primary dark:text-light-text-primary text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                                    API Calls: {data.apiCalls}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Participants Bar */}
                            <div className="relative group">
                              <div 
                                className="w-6 bg-success rounded-t opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                style={{ height: `${participantHeight}%` }}
                              >
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="bg-dark-bg-primary dark:bg-light-bg-primary text-dark-text-primary dark:text-light-text-primary text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                                    Participants: {data.participants}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary font-medium">
                            {dayName}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              
              {/* Chart Legend */}
              {dailyUsage.length > 0 && (
                <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-light-border dark:border-dark-border">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-primary-500 rounded"></div>
                    <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">API Calls</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded"></div>
                    <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Participants</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
        <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
          Recent Activity
        </h2>
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Loading recent activity...</div>
            </div>
          ) : displayedRecentActivity.length > 0 ? (
            displayedRecentActivity.map((activity, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1">
                <p className="text-sm text-light-text-primary dark:text-dark-text-primary">
                  {activity.action}
                </p>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                  {activity.time}
                </p>
              </div>
            </div>
            ))
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                No recent activity
              </p>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                Start using the API to see activity here
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Getting Started */}
      <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-primary-200 dark:border-primary-800">
        <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
          🚀 Getting Started
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Welcome to Customer Connects! Here's how to get started with our video API platform:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start space-x-2">
            <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
            <div>
              <p className="font-medium text-light-text-primary dark:text-dark-text-primary">Create API Key</p>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">Generate your first API key to authenticate requests</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
            <div>
              <p className="font-medium text-light-text-primary dark:text-dark-text-primary">Test API</p>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">Create your first video room using our API</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
            <div>
              <p className="font-medium text-light-text-primary dark:text-dark-text-primary">Integrate</p>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">Use our templates or SDKs to integrate video</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};